import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, makeStyles, Text, tokens } from '@fluentui/react-components';
import { ArrowClockwise20Regular, ArrowDownload20Regular, DataBarVertical24Regular, QrCode20Regular } from '@fluentui/react-icons';
import { IPollResultsQuestion, PollQuestionType } from '../services/DataService';
import { useDataService, useAsync } from '../app/DataServiceContext';
import { useAppToast } from '../app/ToastContext';
import { downloadResultsWorkbook } from '../lib/resultsExport';
import { formatDateTime, formatNumber } from '../lib/format';
import { answersCount, getQuestionTypeLabel, questionsCount } from '../lib/pollLabels';
import PageHeader from '../components/ui/PageHeader';
import Section from '../components/ui/Section';
import StatusBadge from '../components/ui/StatusBadge';
import ErrorBar from '../components/ui/ErrorBar';
import EmptyState from '../components/ui/EmptyState';
import { StatGrid, StatTile } from '../components/ui/StatTile';
import { BlockSkeleton } from '../components/ui/Skeletons';
import { QUESTION_TYPE_ICONS } from '../components/forms/QuestionTypePicker';

const useStyles = makeStyles({
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    head: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
    },
    icon: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '36px',
        height: '36px',
        borderRadius: tokens.borderRadiusMedium,
        color: tokens.colorBrandForeground1,
        backgroundColor: tokens.colorBrandBackground2,
        flexShrink: 0,
    },
    headText: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        minWidth: 0,
    },
    title: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightBase400,
        overflowWrap: 'anywhere',
    },
    meta: {
        display: 'flex',
        gap: '6px',
        flexWrap: 'wrap',
    },
    body: {
        marginTop: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    kpi: {
        display: 'flex',
        alignItems: 'baseline',
        gap: '10px',
        flexWrap: 'wrap',
    },
    kpiValue: {
        fontSize: tokens.fontSizeHero800,
        lineHeight: tokens.lineHeightHero800,
        fontWeight: tokens.fontWeightSemibold,
        letterSpacing: '-0.02em',
        fontVariantNumeric: 'tabular-nums',
    },
    kpiHint: {
        color: tokens.colorNeutralForeground3,
    },
    scale: {
        position: 'relative',
        height: '8px',
        borderRadius: tokens.borderRadiusCircular,
        backgroundColor: tokens.colorNeutralBackground4,
        overflow: 'hidden',
        maxWidth: '480px',
    },
    scaleFill: {
        height: '100%',
        borderRadius: tokens.borderRadiusCircular,
        backgroundColor: tokens.colorBrandBackground,
        transitionProperty: 'width',
        transitionDuration: tokens.durationSlow,
        transitionTimingFunction: tokens.curveEasyEase,
    },
    choice: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        columnGap: '12px',
        rowGap: '6px',
        alignItems: 'center',
    },
    choiceLabel: {
        overflowWrap: 'anywhere',
    },
    choiceValue: {
        fontVariantNumeric: 'tabular-nums',
        color: tokens.colorNeutralForeground2,
        whiteSpace: 'nowrap',
    },
    choiceBar: {
        gridColumn: '1 / -1',
        height: '8px',
        borderRadius: tokens.borderRadiusCircular,
        backgroundColor: tokens.colorNeutralBackground4,
        overflow: 'hidden',
    },
    answers: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    answer: {
        padding: '10px 12px',
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        color: tokens.colorNeutralForeground2,
        lineHeight: tokens.lineHeightBase300,
        overflowWrap: 'anywhere',
    },
    words: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '8px 12px',
    },
    word: {
        color: tokens.colorBrandForeground1,
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: 1.1,
    },
    hint: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
});

const TEXT_PREVIEW = 8;

function pct(value: number): string {
    return `${Math.round(value * 10) / 10}%`;
}

const QuestionResult: React.FC<{ question: IPollResultsQuestion; index: number }> = ({ question, index }) => {
    const styles = useStyles();
    const [expanded, setExpanded] = React.useState(false);

    const isRating = question.questionType === PollQuestionType.Rating;
    const isNps = question.questionType === PollQuestionType.Nps;
    const maxChoice = Math.max(1, ...(question.choices ?? []).map((c) => c.count));
    const words = (question.words ?? []).slice().sort((a, b) => b.count - a.count);
    const maxWeight = Math.max(1, ...words.map((w) => w.weight || w.count));
    const textAnswers = question.textAnswers ?? [];
    const visibleAnswers = expanded ? textAnswers : textAnswers.slice(0, TEXT_PREVIEW);

    const range = isNps ? { min: 0, max: 10 } : { min: question.min ?? 1, max: question.max ?? 5 };
    const avg = typeof question.average === 'number' ? question.average : undefined;
    const avgRatio = avg !== undefined && range.max > range.min ? (avg - range.min) / (range.max - range.min) : 0;

    return (
        <Section>
            <div className={styles.head}>
                <span className={styles.icon} aria-hidden>{QUESTION_TYPE_ICONS[question.questionType as PollQuestionType] ?? QUESTION_TYPE_ICONS[PollQuestionType.Text]}</span>
                <div className={styles.headText}>
                    <Text as="h3" className={styles.title}>{index + 1}. {question.questionText}</Text>
                    <div className={styles.meta}>
                        <Badge appearance="tint" color="brand" size="small" shape="rounded">{getQuestionTypeLabel(question.questionType)}</Badge>
                        <Badge appearance="outline" color="subtle" size="small" shape="rounded">{answersCount(question.totalAnswers ?? 0)}</Badge>
                    </div>
                </div>
            </div>

            <div className={styles.body}>
                {question.totalAnswers === 0 && (
                    <Text className={styles.hint}>На этот вопрос пока никто не ответил.</Text>
                )}

                {(isRating || isNps) && avg !== undefined && (
                    <>
                        <div className={styles.kpi}>
                            <Text className={styles.kpiValue}>{avg.toFixed(isNps ? 2 : 1)}</Text>
                            <Text className={styles.kpiHint}>{isNps ? 'средний балл NPS из 10' : `средняя оценка из ${range.max}`}</Text>
                        </div>
                        <div className={styles.scale} role="img" aria-label={`Средняя оценка ${avg.toFixed(1)} из ${range.max}`}>
                            <div className={styles.scaleFill} style={{ width: `${Math.max(0, Math.min(100, avgRatio * 100))}%` }} />
                        </div>
                    </>
                )}

                {(question.choices?.length ?? 0) > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {question.choices!.map((choice, i) => (
                            <div key={choice.choiceId ?? `${choice.choiceText}-${i}`} className={styles.choice}>
                                <Text className={styles.choiceLabel}>{choice.choiceText || `Вариант ${i + 1}`}</Text>
                                <Text className={styles.choiceValue}>{formatNumber(choice.count)} · {pct(choice.percentage ?? 0)}</Text>
                                <div className={styles.choiceBar} role="img" aria-label={`${choice.choiceText}: ${pct(choice.percentage ?? 0)}`}>
                                    <div className={styles.scaleFill} style={{ width: `${(choice.count / maxChoice) * 100}%` }} />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {textAnswers.length > 0 && (
                    <div className={styles.answers}>
                        {visibleAnswers.map((answer, i) => (
                            <div key={i} className={styles.answer}>{answer}</div>
                        ))}
                        {textAnswers.length > TEXT_PREVIEW && (
                            <div>
                                <Button appearance="subtle" size="small" onClick={() => setExpanded((v) => !v)}>
                                    {expanded ? 'Свернуть' : `Показать все (${textAnswers.length})`}
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {words.length > 0 && (
                    <div className={styles.words} aria-label="Облако слов">
                        {words.map((word) => {
                            const weight = (word.weight || word.count) / maxWeight;
                            return (
                                <span
                                    key={word.text}
                                    className={styles.word}
                                    style={{ fontSize: `${13 + Math.round(weight * 17)}px`, opacity: 0.55 + weight * 0.45 }}
                                    title={`${word.text}: ${word.count}`}
                                >
                                    {word.text}
                                </span>
                            );
                        })}
                    </div>
                )}
            </div>
        </Section>
    );
};

const PollResultsPage: React.FC = () => {
    const styles = useStyles();
    const navigate = useNavigate();
    const toast = useAppToast();
    const dataService = useDataService();
    const { id = '' } = useParams<{ id: string }>();

    const poll = useAsync(() => dataService.getPoll(id), [id]);
    const results = useAsync(() => dataService.getPollResults(id), [id]);
    const [downloading, setDownloading] = React.useState(false);

    const data = results.data;
    const questions = data?.questions ?? [];
    const ratingAverages = questions
        .filter((q) => q.questionType === PollQuestionType.Rating && typeof q.average === 'number')
        .map((q) => q.average as number);
    const npsAverages = questions
        .filter((q) => q.questionType === PollQuestionType.Nps && typeof q.average === 'number')
        .map((q) => q.average as number);
    const mean = (list: number[]): number | undefined => (list.length ? list.reduce((a, b) => a + b, 0) / list.length : undefined);
    const ratingMean = mean(ratingAverages);
    const npsMean = mean(npsAverages);
    const detailsTo = `/polls/${encodeURIComponent(id)}`;

    const download = async (): Promise<void> => {
        if (!poll.data) return;
        try {
            setDownloading(true);
            const fresh = await dataService.getPollResults(id);
            await downloadResultsWorkbook(poll.data, fresh);
        } catch (e) {
            toast.error('Не удалось скачать результаты', (e as Error)?.message);
        } finally {
            setDownloading(false);
        }
    };

    const hasResponses = !!data && data.hasResults !== false && (data.totalResponses ?? 0) > 0;

    return (
        <>
            <PageHeader
                backTo={{ to: detailsTo, label: 'К опросу' }}
                title="Результаты"
                subtitle={poll.data?.Title}
                badges={poll.data && <StatusBadge state={poll.data.State} />}
                actions={[
                    { key: 'refresh', label: 'Обновить', icon: <ArrowClockwise20Regular />, iconOnly: true, appearance: 'secondary', disabled: results.loading, onClick: results.reload },
                    { key: 'download', label: 'Скачать XLSX', icon: <ArrowDownload20Regular />, appearance: 'primary', disabled: !poll.data || !hasResponses || downloading, onClick: () => { void download(); } },
                ]}
            />

            <ErrorBar message={results.error ?? poll.error} title="Не удалось загрузить результаты" onRetry={() => { results.reload(); poll.reload(); }} />

            {results.loading && !data && (
                <Section><BlockSkeleton lines={6} /></Section>
            )}

            {data && !hasResponses && (
                <EmptyState
                    icon={<DataBarVertical24Regular />}
                    title="Ответов пока нет"
                    description="Поделитесь ссылкой или покажите QR-код участникам. Результаты появятся здесь сразу после первого ответа."
                    actions={(
                        <Button appearance="primary" icon={<QrCode20Regular />} onClick={() => navigate(`${detailsTo}/qr`)}>
                            Открыть QR-код
                        </Button>
                    )}
                />
            )}

            {data && hasResponses && (
                <>
                    <StatGrid>
                        <StatTile label="Ответов" value={formatNumber(data.totalResponses)} hint={poll.data?.Created ? `опрос создан ${formatDateTime(poll.data.Created)}` : undefined} />
                        <StatTile label="Вопросов" value={String(questions.length)} hint={questionsCount(questions.length)} />
                        {ratingMean !== undefined && <StatTile label="Средняя оценка" value={ratingMean.toFixed(1)} hint="по вопросам с рейтингом" />}
                        {npsMean !== undefined && <StatTile label="NPS" value={npsMean.toFixed(1)} hint="средний балл из 10" />}
                    </StatGrid>

                    <div className={styles.list}>
                        {questions.map((question, index) => (
                            <QuestionResult key={question.questionId ?? index} question={question} index={index} />
                        ))}
                    </div>
                </>
            )}
        </>
    );
};

export default PollResultsPage;
